import { Immutable } from "immer";
import React from "react";
import { CardSlider } from "tsi-common-react/src/components/CardSlider/index";

import type { CardSlider as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * REWIRE of the legacy `card-slider.html`.
 *
 * Legacy rendered:
 *   <section class="card-slider" data-block-class="card-slider-block-js--{i}">
 *     <h1 class="card-slider__title l-capped-width">{{value.title|safe}}</h1>
 *     {% if value.columns %}
 *       {% for column in value.columns %}
 *         <div aria-hidden class="card-slider-block-js--{i}"
 *              data-image-url=... data-copy=...></div>   (scraper carriers)
 *       {% endfor %}
 *       <div class="card-slider__carousel l-capped-width"
 *            data-place-react="card-slider"></div>
 *     {% endif %}
 *   </section>
 *
 * The old DOM-placement widget scraped those hidden `data-image-url`/`data-copy`
 * divs into `ICardSlider[]` and mounted the shared
 * `tsi-common-react/src/components/CardSlider`. The rewire passes the data directly
 * as `images`, so the hidden scraper carrier divs, the `data-block-class`, and
 * the `data-place-react="card-slider"` mount point are all dead data plumbing —
 * deliberately dropped. They rendered nothing visible, so the user-facing DOM is
 * unchanged.
 *
 * Markup parity preserved: the `<section class="card-slider">` wrapper, the
 * always-present `<h1 class="card-slider__title l-capped-width">` (raw HTML via
 * `|safe`), and the `<div class="card-slider__carousel l-capped-width">` wrapper
 * gated on `columns`. The shared `CardSlider` renders only its inner
 * `card-slider__carousel-content` — it does NOT include this section/title/
 * carousel chrome — so we wrap it here.
 */
export const CardSliderBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const columns = v.columns ?? [];

    const images = columns.map((col) => ({
        // `image.url` is the original rendition URL (matches legacy
        // `{% image column.image original %}`); the serialized renditions map is
        // width-keyed, so there is no literal `original` key to fall back to.
        imageUrl: col.image?.url ?? "",
        copy: col.copy ?? "",
    }));

    return (
        <section
            // `card-slider` stays as a test hook: HomePageBody.test.tsx selects it.
            className={concatClassNames([styles.cardSlider, "card-slider"])}
        >
            <h1
                className={concatClassNames([
                    styles.cardSliderTitle,
                    "l-capped-width",
                ])}
                dangerouslySetInnerHTML={{ __html: v.title ?? "" }}
            ></h1>
            {columns.length > 0 && (
                <div className="card-slider__carousel l-capped-width">
                    <CardSlider images={images} />
                </div>
            )}
        </section>
    );
};
